import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Collapse,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useOrderForm, addresses, paymentMethods } from "../utils/SummaryUtils";
import { useNavigate } from "react-router-dom";
import CustomDatePicker from "./CustomerDatePicker";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import CouponField from "./CouponField";
import { useDiscountManager } from "../hooks/useDiscountManager";
import { effectiveDeliveryRate } from "../utils/deliveryFee";

export default function WindowsOrderSummary({
  cart,
  sendCartToBackend,
  clearCart,
  addresses,
  currentStep,
  setCurrentStep,
}) {
  useEffect(() => {
    setCurrentStep(1);
  }, []);

  const { formData, errors, handleChange, validateStep, setFormData } =
    useOrderForm();
  const navigate = useNavigate();
  const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const [orderId, setOrderId] = useState(null);
  const fieldsDisabled = currentStep === 2;
  const { t } = useTranslation();

  const {
    discounts,
    loading,
    error: discountError,
    refetch,
    appliedDiscount: discount,
    appliedCoupon,
    applyCoupon,
    couponCodeUsed,
    warning,
  } = useDiscountManager();

  // Sanitize input function
  const sanitizeInput = (input) => {
    if (typeof input === "string") {
      return DOMPurify.sanitize(input.trim());
    }
    return input;
  };

  // Modify handleChange to include sanitization
  const handleSanitizedChange = (field) => (event) => {
    const sanitizedValue = sanitizeInput(event.target.value);
    handleChange(field)({ target: { value: sanitizedValue } });
  };

  useEffect(() => {
    if (paymentMethods.length === 1 && !formData.payment) {
      setFormData((prevData) => ({
        ...prevData,
        payment: t(paymentMethods[0].key),
      }));
    }
  }, []);

  const validateAllFields = () => {
    const isStep1Valid = validateStep(1);
    const isStep2Valid = validateStep(2);

    return isStep1Valid && isStep2Valid;
  };

  const handleOrderSubmission = async () => {
    try {
      const discountObject = ((appliedCoupon??discount))??null;
      const discountTotal = (
                          cart.reduce(
                            (sum, item) =>
                              sum +
                              (item.baseBundleId ? 0 : item.price * item.qty),
                            0
                          ) *
                          (((appliedCoupon?.value ?? discount?.value) ) / 100)
                        );
      console.log(couponCodeUsed);
      const result = await sendCartToBackend(formData,{discount:discountObject ,total:discountTotal, code:couponCodeUsed});
      if (result.success) {
        setCurrentStep(2);
        clearCart();

        setOrderId(result.data.orderId);
      } else {
        switch (result.code) {
          case 409:
            setCurrentStep(2);
            clearCart();

            setOrderId(result.data.orderId);
            break;
          case 401:
            navigate("/OrderSummary");
            break;
          default:
            // Handle other error cases
            console.error("Unexpected error:", result);
          // Maybe set an error state and display to user
        }
      }
    } catch (error) {
      console.error("Error in order submission:", error);
      // Handle network errors or unexpected exceptions
      // Maybe set an error state and display to user
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center", // vertical centering
        alignItems: "center", // horizontal centering
        backgroundColor: "#f3faf5", // optional
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1150px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          px:6,
          pt: 2,
        }}
      >
        <Box
          component="img"
          src="/logo512.png"
          alt="Logo"
          sx={{ width: 48, height: 48 }}
        />
        <IconButton
          onClick={() => {
            window.location.href = "/";
          }}
        >
          <HomeOutlinedIcon sx={{ width: 36, height: 36 }} />
        </IconButton>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 1,
            maxWidth: "1150px",
            borderRadius: "2rem",
            display: "flex",
            flexDirection: "row",
            gap: 1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#ffffff",
              p: 3,
              width: "45%",
              border: "1px solid rgba(27, 122, 61, 0.12)",
              borderRadius: "1.25rem",
              boxShadow: "0 10px 32px rgba(15, 77, 40, 0.08)",
            }}
          >
            <Box>
              <Typography
                fontWeight="bold"
                mb={2}
                sx={{
                  color: "#1b7a3d",
                  fontSize: "clamp(1.2rem,1.5vw,1.2rem)",
                }}
              >
                {t("contactInfo")}
              </Typography>
              <TextField
                fullWidth
                disabled={fieldsDisabled}
                label={t("firstName")}
                value={formData.firstName}
                onChange={handleSanitizedChange("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                disabled={fieldsDisabled}
                label={t("familyName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
                value={formData.lastName}
                onChange={handleSanitizedChange("lastName")}
                sx={{ mb: 2, fontFamily: "inherit" }}
              />
              <TextField
                fullWidth
                disabled={fieldsDisabled}
                error={!!errors.phone}
                helperText={errors.phone}
                label={t("phoneNumber")}
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 10); // Limit to 10 chars
                  handleSanitizedChange("phone")({ target: { value } });
                }}
                sx={{ mb: 2, fontFamily: "Tajawal" }}
              />
              <TextField
                fullWidth
                disabled={fieldsDisabled}
                error={!!errors.email}
                helperText={errors.email}
                label={t("optionalEmail")}
                value={formData.email}
                onChange={handleSanitizedChange("email")}
                sx={{ mb: 2, fontFamily: "Tajawal" }}
              />
              <TextField
                select
                error={!!errors.address}
                helperText={errors.address}
                fullWidth
                disabled={fieldsDisabled}
                label={t("pickDeliveryAddress")}
                value={formData.address}
                onChange={(e) => {
                  const selectedName = e.target.value;
                  const selectedAddr = addresses.find(
                    (addr) => addr.name === selectedName
                  );
                  setFormData((prev) => ({
                    ...prev,
                    address: selectedName,
                    deliveryRate: selectedAddr?.deliveryRate || 0,
                  }));
                }}
                sx={{ mb: 2, fontFamily: "Tajawal" }}
              >
                {addresses.map((addr) => (
                  <MenuItem
                    key={addr.name}
                    value={addr.name}
                    dir="rtl"
                    fontFamily="Tajawal"
                  >
                    {addr.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              display="flex"
              flexDirection="row"
              justifyContent={"space-evenly"}
            >
              <Box>
                <Box
                  item
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 0.5,
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    mt={1}
                    mb={2}
                    sx={{
                      color: "#1b7a3d",
                      fontSize: "clamp(1.2rem,1.5vw,1.2rem)",
                      fontFamily: "Tajawal",
                    }}
                  >
                    {t("pickPaymentMethod")}
                  </Typography>
                </Box>

                <RadioGroup
                  disabled={fieldsDisabled}
                  value={formData.payment}
                  onChange={handleChange("payment")}
                >
                  {paymentMethods.map((method) => (
                    <FormControlLabel
                      key={t(method.key)}
                      value={t(method.key)}
                      control={<Radio />}
                      label={t(method.key)}
                    />
                  ))}
                </RadioGroup>
                {errors.payment && (
                  <Typography
                    color="error"
                    sx={{ mt: 1, fontFamily: "Tajawal" }}
                  >
                    {errors.payment}
                  </Typography>
                )}
              </Box>
              <Box>
                <Box>
                  <Typography
                    fontWeight="bold"
                    mt={1}
                    disabled={fieldsDisabled}
                    // mb={2}
                    sx={{
                      color: "#1b7a3d",
                      fontSize: "clamp(1.1rem,1.5vw,1.2rem)",
                      fontFamily: "Tajawal",
                    }}
                  >
                    {t("pickDeliveryDate")}
                  </Typography>
                </Box>
                <CustomDatePicker
                  onDateChange={(newDate) => {
                    // Format the date as needed, for example to ISO string
                    const formattedDate = newDate
                      ? newDate.toISOString()
                      : null;
                    handleChange("deliveryDate")(formattedDate);
                  }}
                />
                {errors.deliveryDate && (
                  <Typography
                    color="error"
                    sx={{ mt: 1, fontFamily: "Tajawal" }}
                  >
                    {errors.deliveryDate}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ p: 2 }}></Box>
            <TextField
              fullWidth
              label={t("optionalNotes")}
              value={formData.note}
              disabled={fieldsDisabled}
              onChange={(e) => {
                const value = e.target.value.slice(0, 200); // Limit to 200 chars
                handleChange("note")({ target: { value } });
              }}
              sx={{ mb: 2, fontFamily: "Tajawal" }}
              multiline
              minRows={3}
              helperText={`${formData.note.length}/200`}
            />
          </Box>
          {/* currentStep 3: Summary */}
          <Box
            sx={{
              backgroundColor: "#ffffff",
              width: "55%",
              p: 5,
              border: "1px solid rgba(27, 122, 61, 0.12)",
              borderRadius: "1.25rem",
              boxShadow: "0 10px 32px rgba(15, 77, 40, 0.08)",
            }}
          >
            <Collapse in={currentStep === 1} sx={{ pb: 1 }}>
              <Box
                item
                sx={{
                  pl: 0.5,
                }}
              >
                <Typography
                  fontWeight="bold"
                  mt={1}
                  mb={1}
                  sx={{
                    fontSize: "clamp(2rem,2.2vw,2.7rem)",
                    textAlign: "center",
                    color: "#1b7a3d",
                  }}
                >
                  {t("orderSummary")}
                </Typography>
              </Box>

              <Box
                mb={2}
                sx={{
                  p: 2,
                  pb: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  borderBottom: "1px solid rgba(27, 122, 61, 0.12)",
                  borderLeft: "1px solid rgba(27, 122, 61, 0.12)",
                  backgroundColor: "#f3faf5",
                  borderRadius: "1rem",
                }}
              >
                <Box
                  item
                  display="flex"
                  justifyContent="space-between"
                  sx={{ pl: 2, pr: 2 }}
                >
                  <Typography>🧑{t("name")}:</Typography>
                  <Typography>
                    {sanitizeInput(formData.firstName)}{" "}
                    {sanitizeInput(formData.lastName)}
                  </Typography>
                </Box>
                <Box
                  item
                  display="flex"
                  justifyContent="space-between"
                  sx={{ pl: 2, pr: 2 }}
                >
                  <Typography>📞 {t("mobile")}: </Typography>
                  <Typography>{sanitizeInput(formData.phone)}</Typography>
                </Box>
                {formData.email && (
                  <Box
                    item
                    display="flex"
                    justifyContent="space-between"
                    sx={{ pl: 2, pr: 2 }}
                  >
                    <Typography>✉️ {t("email")}:</Typography>
                    <Typography> {formData.email}</Typography>
                  </Box>
                )}
                <Box
                  item
                  display="flex"
                  justifyContent="space-between"
                  sx={{ pl: 2, pr: 2 }}
                >
                  <Typography>📍 {t("address")}: </Typography>
                  <Typography> {formData.address}</Typography>
                </Box>
                <Box
                  item
                  display="flex"
                  justifyContent="space-between"
                  sx={{ pl: 2, pr: 2 }}
                >
                  <Typography>📆 {t("deliveryDate")}:</Typography>
                  <Typography>
                    {formData.deliveryDate
                      ? dayjs(formData.deliveryDate).format("DD/MM/YYYY")
                      : ""}
                  </Typography>
                </Box>
                <Box
                  item
                  display="flex"
                  justifyContent="space-between"
                  sx={{ pl: 2, pr: 2 }}
                >
                  <Typography>💳 {t("payment")}: </Typography>
                  <Typography>{formData.payment}</Typography>
                </Box>
                {formData.note && (
                  <Box
                    item
                    display="flex"
                    justifyContent="space-between"
                    sx={{ pl: 2, pr: 2 }}
                  >
                    <Typography>📝 {t("note")}: </Typography>
                    <Typography>{formData.note}</Typography>
                  </Box>
                )}
              </Box>

              <Box>
                <CouponField onApply={applyCoupon} />
              </Box>
              <Box
                mb={2}
                sx={{
                  p: 2,
                  backgroundColor: "#f3faf5",
                  borderBottom: "1px solid rgba(27, 122, 61, 0.12)",
                  borderLeft: "1px solid rgba(27, 122, 61, 0.12)",
                  borderRadius: "1rem",
                }}
              >
                <Box sx={{ borderBottom: "1px solid rgba(27, 122, 61, 0.12)" }}>
                  {cart.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box display="flex" flexDirection="column" sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" flexDirection="row" flexWrap="wrap">
                          <Typography sx={{ textAlign: "right" }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ textAlign: "right", mx: 1.5 }}>
                            x
                          </Typography>
                          <Typography sx={{ textAlign: "right" }}>
                            {item.qty}
                          </Typography>
                        </Box>
                        {item.skipMealSelection && item.description && (
                          <Typography
                            sx={{
                              textAlign: "right",
                              mt: 0.5,
                              fontFamily: "Tajawal",
                              color: "text.secondary",
                              fontSize: "0.9rem",
                            }}
                          >
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ fontWeight: "bold", flexShrink: 0, ml: 1 }}>
                        {item.qty * item.price} ₪
                      </Typography>
                    </Box>
                  ))}
                  {(discount || appliedCoupon) &&
                    ((appliedCoupon?.value ?? discount?.value) > 0) > 0 && (
                      <Box
                        sx={{
                          mb: 1,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography sx={{ textAlign: "right" }}>
                          {(appliedCoupon?.description ?? discount?.description)}
                        </Typography>

                        <Typography sx={{ fontWeight: "bold" }}>
                          {(
                            cart.reduce(
                              (sum, item) =>
                                sum +
                                (item.baseBundleId ? 0 : item.price * item.qty),
                              0
                            ) *
                            (((appliedCoupon?.value ?? discount?.value) ) / 100)
                          ).toFixed(0)}
                          - ₪
                        </Typography>
                      </Box>
                    )}
                  {effectiveDeliveryRate(cart, formData.deliveryRate) > 0 && (
                    <Box
                      sx={{
                        mb: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ textAlign: "right" }}>
                        {t("deliveryRate")}
                      </Typography>

                      <Typography sx={{ fontWeight: "bold" }}>
                        {effectiveDeliveryRate(cart, formData.deliveryRate)} ₪
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    pt: 1,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography fontWeight="bold" textAlign="right">
                    {t("totalAmount")}
                  </Typography>
                  <Typography fontWeight="bold" textAlign="right">
                    {(() => {
                      const discountValue = Number(
                        (discount || appliedCoupon)?.value || 0
                      );
                      const deliveryRate = effectiveDeliveryRate(
                        cart,
                        formData.deliveryRate
                      );
                      const mealsTotal = cart.reduce(
                        (sum, item) =>
                          sum + (item.baseBundleId ? 0 : item.price * item.qty),
                        0
                      );
                      const bundlesTotal = cart.reduce(
                        (sum, item) =>
                          sum + (item.baseBundleId ? item.price * item.qty : 0),
                        0
                      );
                      const discountedMealsTotal =
                        mealsTotal * (1 - discountValue / 100);
                      const finalTotal =
                        discountedMealsTotal + bundlesTotal + deliveryRate;
                      return isNaN(finalTotal)
                        ? "N/A"
                        : finalTotal.toFixed(2) + " ₪";
                    })()}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                className="dc-btn-primary"
                sx={{ mt: 3, py: 1.4, borderRadius: 3, fontSize: "1.05rem" }}
                onClick={async () => {
                  if (validateAllFields()) {
                    await handleOrderSubmission();
                  } else {
                    console.log(
                      "Please fill all required fields and ensure cart is not empty"
                    );
                  }
                }}
              >
                {t("submitOrder")}
              </Button>
            </Collapse>

            <Collapse in={currentStep === 2} sx={{ pb: 1 }}>
              <Box>
                <Typography
                  fontWeight="bold"
                  mt={1}
                  mb={2}
                  sx={{
                    fontSize: "clamp(1.2rem,1.5vw,1.2rem)",
                    textAlign: "center",
                    color: "#1b7a3d",
                    display: "flex",
                  }}
                >
                  {t("successfulOrder")}
                </Typography>
                <Typography sx={{ fontSize: "clamp(1rem,1.5vw,1.2rem)" }}>
                  {t("thankYouForYourOrder")}
                </Typography>
                <Typography sx={{ fontSize: "clamp(1rem,1.5vw,1.2rem)" }}>
                  {t("orderId")}: {orderId}
                </Typography>
                <Button
                  fullWidth
                  color="primary"
                  sx={{ minHeight: "2rem" }}
                  onClick={() => {
                    navigate("/");
                  }}
                >
                  {t("goBack")}
                </Button>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
